import { type ReactNode, type ComponentType, type SVGProps } from 'react'

interface PageLayoutProps {
  /** HeroIcon component for the header */
  icon: ComponentType<SVGProps<SVGSVGElement>>
  /** Page title displayed in header */
  title: string
  /** Optional subtitle/description */
  subtitle?: string
  /** Main content */
  children: ReactNode
  /** Optional sidebar content (renders left of main content) */
  sidebar?: ReactNode
  /** Maximum width class (default: max-w-6xl) */
  maxWidth?: string
  /** Whether to use full height layout for sidebar (default: false) */
  fullHeight?: boolean
}

/**
 * Consistent page layout component used across feature pages.
 * Supports optional sidebar for pages like AI Chat.
 */
export function PageLayout({
  icon: Icon,
  title,
  subtitle,
  children,
  sidebar,
  maxWidth = 'max-w-6xl',
  fullHeight = false,
}: PageLayoutProps) {
  // Full height layout with sidebar (for chat-style pages)
  if (sidebar && fullHeight) {
    return (
      <div className="flex h-[calc(100vh-4rem)]">
        {sidebar}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex-shrink-0 px-6 py-4 border-b border-app-line">
            <div className="flex items-center gap-3">
              <Icon className="h-8 w-8 text-app-accent" />
              <div>
                <h1 className="text-2xl font-bold text-app-ink font-display">{title}</h1>
                {subtitle && (
                  <p className="text-sm text-app-muted">{subtitle}</p>
                )}
              </div>
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    )
  }

  // Standard layout (no sidebar)
  return (
    <div className={`mx-auto ${maxWidth} space-y-6`}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <Icon className="h-8 w-8 text-app-accent" />
        <div>
          <h1 className="text-2xl font-bold text-app-ink font-display">{title}</h1>
          {subtitle && (
            <p className="text-sm text-app-muted">{subtitle}</p>
          )}
        </div>
      </div>

      {/* Content */}
      {children}
    </div>
  )
}
